import type { RouteRecordRaw } from 'vue-router';

/**
 * Inventory core screens (M2 WP2.6): warehouses/locations, stock on hand, ledger, movements, lots
 * and handling-unit genealogy, and the admin rebuild/compare integrity screen. Kept in its own file
 * per the parallel-work convention — `router.ts` only spreads this array into the app-shell
 * `children`, so the documents/reservations, valuation/count and textile-rolls work packages can add
 * their own screens later without touching this file or each other's.
 *
 * Only routes for APIs that exist in the merged Inventory core (ADR-037) are added here — no routes
 * for receipts/issues/transfers-as-documents, valuation, physical count or textile roll screens yet.
 */
export const inventoryRoutes: RouteRecordRaw[] = [
  {
    path: 'inventory/warehouses',
    name: 'inventory-warehouses',
    component: () => import('../pages/inventory/WarehousesPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/warehouses/:id',
    name: 'inventory-warehouse-detail',
    component: () => import('../pages/inventory/WarehouseDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/balances',
    name: 'inventory-balances',
    component: () => import('../pages/inventory/BalancesPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/ledger',
    name: 'inventory-ledger',
    component: () => import('../pages/inventory/LedgerPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/movements',
    name: 'inventory-movements',
    component: () => import('../pages/inventory/MovementsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/movements/new',
    name: 'inventory-movement-new',
    component: () => import('../pages/inventory/CreateMovementPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/movements/:id',
    name: 'inventory-movement-detail',
    component: () => import('../pages/inventory/MovementDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/lots',
    name: 'inventory-lots',
    component: () => import('../pages/inventory/LotsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/handling-units',
    name: 'inventory-handling-units',
    component: () => import('../pages/inventory/HandlingUnitsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/handling-units/:id',
    name: 'inventory-handling-unit-detail',
    component: () => import('../pages/inventory/HandlingUnitDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/integrity',
    name: 'inventory-integrity',
    component: () => import('../pages/inventory/IntegrityPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  // -- Documents & reservations (M2 WP2.6 remainder A, ADR-038) -------------------------------------
  {
    path: 'inventory/documents',
    name: 'inventory-documents',
    component: () => import('../pages/inventory/DocumentsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/documents/new/:type',
    name: 'inventory-document-new',
    component: () => import('../pages/inventory/CreateDocumentPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/documents/:id',
    name: 'inventory-document-detail',
    component: () => import('../pages/inventory/DocumentDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/document-policy/:companyId',
    name: 'inventory-document-policy',
    component: () => import('../pages/inventory/DocumentPolicyPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/reservations',
    name: 'inventory-reservations',
    component: () => import('../pages/inventory/ReservationsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/reservations/:id',
    name: 'inventory-reservation-detail',
    component: () => import('../pages/inventory/ReservationDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
  // ---- valuation & finance posting requests (M2 WP2.6 remainder B, ADR-040) --------------------------
  {
    path: 'inventory/valuation/config',
    name: 'inventory-valuation-config',
    component: () => import('../pages/inventory/valuation/ValuationConfigPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/valuation/balances',
    name: 'inventory-valuation-balances',
    component: () => import('../pages/inventory/valuation/ValuationBalancesPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/valuation/entries',
    name: 'inventory-valuation-entries',
    component: () => import('../pages/inventory/valuation/ValuationEntriesPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/valuation/posting-requests',
    name: 'inventory-valuation-posting-requests',
    component: () => import('../pages/inventory/valuation/PostingRequestsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/valuation/reconciliation',
    name: 'inventory-valuation-reconciliation',
    component: () => import('../pages/inventory/valuation/ValuationReconciliationPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/valuation/integrity',
    name: 'inventory-valuation-integrity',
    component: () => import('../pages/inventory/valuation/ValuationIntegrityPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  // ---- physical count (M2 WP2.6 remainder B, ADR-040) -------------------------------------------------
  {
    path: 'inventory/counts',
    name: 'inventory-counts',
    component: () => import('../pages/inventory/counts/CountSessionsPage.vue'),
    meta: { requiresModule: 'inventory' },
  },
  {
    path: 'inventory/counts/:id',
    name: 'inventory-count-detail',
    component: () => import('../pages/inventory/counts/CountSessionDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'inventory' },
  },
];
